import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { neighborhoods, neighborhoodCategories, featuredListings, marketStats } from '../../mocks/realEstate';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function RealEstatePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All Neighborhoods');
  const [expandedNeighborhood, setExpandedNeighborhood] = useState<number | null>(null);

  useSEO({
    title: 'Roseville CA Real Estate - Homes for Sale, Housing Market & Property Listings',
    description: 'Explore Roseville California real estate. Find homes for sale, housing market trends, property listings, and real estate agents in Roseville CA. Search houses, condos, and new construction.',
    keywords: 'Roseville CA real estate, homes for sale Roseville California, Roseville housing market, property listings Roseville, houses for sale Roseville, condos Roseville, real estate agents Roseville CA',
    canonical: '/real-estate',
    ogImage: 'https://public.readdy.ai/ai/img_res/d12775994f7b19555b395c59bd4a015f.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Real Estate & Homes for Sale',
          description: 'Real estate listings and housing market information for Roseville, California',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/real-estate`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Real Estate', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/real-estate` },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is the median home price in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The median home price in Roseville, CA is approximately $620,000, with year-over-year growth of around 5.2%. Prices vary by neighborhood, with newer developments in West Roseville typically priced higher.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is Roseville CA a good place to buy a home?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, Roseville is consistently ranked among the best places to live in California. It offers top-rated schools, low crime rates, strong job market, 267 sunny days per year, and a growing community with excellent amenities.',
              },
            },
            {
              '@type': 'Question',
              name: 'What neighborhoods are in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Roseville has diverse neighborhoods including Historic Old Town, West Roseville, East Roseville, Woodcreek, Sierra Gardens, and the Galleria District. Each offers different home styles, price ranges, and community amenities.',
              },
            },
          ],
        },
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredNeighborhoods = selectedCategory === 'All Neighborhoods'
    ? neighborhoods
    : neighborhoods.filter(n => n.category === selectedCategory);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="font-black">ROSEVILLE</span>
                <span className="text-teal-500">.org</span>
              </div>
            </Link>
            
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Home</Link>
              <Link to="/recreation" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Recreation</Link>
              <Link to="/real-estate" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-teal-600' : 'text-teal-300'
              }`}>Real Estate</Link>
            </div>

            <Link to="/" className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
              Get Guide
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20beautiful%20California%20suburban%20neighborhood%20with%20modern%20homes%2C%20tree-lined%20streets%2C%20community%20parks%2C%20Sierra%20Nevada%20foothills%20in%20background%2C%20golden%20hour%20lighting%2C%20real%20estate%20photography&width=1920&height=1080&seq=rehero001&orientation=landscape"
            alt="Roseville Real Estate"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Find Your Home in Roseville
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Discover why Roseville is one of California's most desirable places to live. 
            Explore neighborhoods, view listings, and find your perfect home.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#neighborhoods" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer">
              Explore Neighborhoods
            </a>
            <a href="#listings" className="bg-white/20 hover:bg-white/30 text-white px-8 py-3 rounded-full font-semibold transition-colors backdrop-blur-sm whitespace-nowrap cursor-pointer">
              View Listings
            </a>
          </div>
        </div>
      </section>

      {/* Market Stats */}
      <section className="py-12 bg-teal-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">{formatPrice(marketStats.medianPrice)}</div>
              <div className="text-teal-100 text-sm">Median Price</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">+{marketStats.priceChange}%</div>
              <div className="text-teal-100 text-sm">YoY Growth</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">{marketStats.avgDaysOnMarket}</div>
              <div className="text-teal-100 text-sm">Avg Days on Market</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">{marketStats.inventoryCount}</div>
              <div className="text-teal-100 text-sm">Active Listings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">{marketStats.soldLastMonth}</div>
              <div className="text-teal-100 text-sm">Sold Last Month</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">{marketStats.listToSaleRatio}%</div>
              <div className="text-teal-100 text-sm">List-to-Sale Ratio</div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-teal-500/40">
            <p className="text-sm text-teal-200/80 text-center">
              Data provided by Zillow, Realtor.com, and Walk Score&reg;. Market statistics are approximate and updated periodically.
              <a href="/data-sources" className="underline hover:text-white ml-1 transition-colors">View all sources</a>
            </p>
            <p className="text-xs text-teal-200/50 text-center mt-1">
              <i className="ri-time-line mr-1"></i>Data last updated: February 10, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Why Roseville */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Live in Roseville?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Consistently ranked among California's best places to live
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-graduation-cap-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Top-Rated Schools</h3>
              <p className="text-sm text-gray-600">Award-winning school districts with high graduation rates</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Safe Community</h3>
              <p className="text-sm text-gray-600">Low crime rates and family-friendly neighborhoods</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-briefcase-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Strong Economy</h3>
              <p className="text-sm text-gray-600">Growing job market with major employers nearby</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-sun-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Great Weather</h3>
              <p className="text-sm text-gray-600">267 sunny days per year with mild winters</p>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section id="neighborhoods" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Explore Neighborhoods</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From new developments to established communities, find the perfect neighborhood for your lifestyle
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {neighborhoodCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Neighborhoods Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNeighborhoods.map((neighborhood) => (
              <div
                key={neighborhood.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={neighborhood.image}
                    alt={neighborhood.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                      {neighborhood.category}
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-lg">
                    <span className="text-sm font-bold text-gray-900">{neighborhood.priceRange}</span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{neighborhood.name}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{neighborhood.description}</p>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1 text-sm">
                      <i className="ri-walk-line text-teal-500"></i>
                      <span className="text-gray-700">Walk: {neighborhood.walkScore}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <i className="ri-bus-line text-teal-500"></i>
                      <span className="text-gray-700">Transit: {neighborhood.transitScore}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-sm text-gray-600 mb-1">
                      <strong>Schools:</strong> {neighborhood.schools}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Avg Price:</strong> {neighborhood.avgPrice}
                    </div>
                  </div>

                  {expandedNeighborhood === neighborhood.id && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Highlights:</h4>
                      <div className="flex flex-wrap gap-2">
                        {neighborhood.highlights.map((highlight, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-teal-50 text-teal-700 text-xs rounded-full"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                      <div className="mt-3">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Home Types:</h4>
                        <div className="flex flex-wrap gap-2">
                          {neighborhood.homeTypes.map((type, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setExpandedNeighborhood(expandedNeighborhood === neighborhood.id ? null : neighborhood.id)}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {expandedNeighborhood === neighborhood.id ? 'Show Less' : 'View Details'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      <section id="listings" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Listings</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Browse some of the best homes currently on the market in Roseville
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredListings.map((listing) => (
              <div
                key={listing.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={listing.image}
                    alt={listing.address}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 text-white text-xs font-semibold rounded-full ${
                      listing.status === 'For Sale' ? 'bg-teal-500' : 'bg-amber-500'
                    }`}>
                      {listing.status}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-lg">
                    <span className="text-xs text-gray-600">{listing.daysOnMarket} days</span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="text-2xl font-bold text-gray-900 mb-1">
                    {formatPrice(listing.price)}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{listing.address}</h3>
                  <p className="text-sm text-gray-500 mb-4">{listing.neighborhood}</p>

                  <div className="flex items-center gap-4 text-sm text-gray-700">
                    <div className="flex items-center gap-1">
                      <i className="ri-hotel-bed-line text-teal-500"></i>
                      <span>{listing.beds} beds</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-drop-line text-teal-500"></i>
                      <span>{listing.baths} baths</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-ruler-line text-teal-500"></i>
                      <span>{listing.sqft.toLocaleString()} sqft</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://www.zillow.com/roseville-ca/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span>View All Listings</span>
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Homebuyer Resources</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything you need to make your move to Roseville smooth and successful
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">First-Time Buyer Guide</h3>
              <p className="text-sm text-gray-600">Step-by-step guide to purchasing your first home in Roseville</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calculator-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Mortgage Calculator</h3>
              <p className="text-sm text-gray-600">Estimate your monthly payments and affordability</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-school-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">School Information</h3>
              <p className="text-sm text-gray-600">Learn about Roseville's top-rated school districts</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-truck-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Relocation Guide</h3>
              <p className="text-sm text-gray-600">Tips for moving to Roseville from out of area</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-gear-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Home Inspection Tips</h3>
              <p className="text-sm text-gray-600">What to look for when inspecting a potential home</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-community-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">HOA Information</h3>
              <p className="text-sm text-gray-600">Understanding homeowner associations in Roseville</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Find Your Dream Home?</h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Connect with local real estate professionals who know Roseville inside and out. 
            Start your home search today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.realtor.com/realestateandhomes-search/Roseville_CA"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Search Homes
            </a>
            <a
              href="https://www.roseville.ca.us/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-700 hover:bg-teal-800 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              City Resources
            </a>
          </div>
        </div>
      </section>

      <MailchimpNewsletter />

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-800 via-teal-900 to-teal-800 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="text-2xl font-bold mb-4 block cursor-pointer">
                <span className="font-black">ROSEVILLE</span>
                <span className="text-amber-400">.org</span>
              </Link>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Your guide to living in Roseville, California
              </p>
              <p className="text-white/80 text-sm">
                5098 Foothills Blvd, Suite 3-402<br />
                Roseville, California 95747
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/recreation" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Recreation</Link></li>
                <li><Link to="/real-estate" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Real Estate</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Real Estate</h4>
              <ul className="space-y-2">
                <li><a href="#neighborhoods" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Neighborhoods</a></li>
                <li><a href="#listings" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Listings</a></li>
                <li><a href="#" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Market Report</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Connect</h4>
              <div className="flex space-x-3">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-white/60 text-sm">
                © 2025 City of Roseville, California. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
